import { persistZebraEvent } from '@/entities/order/api/order.api'
import { isPermanentRejection } from '@/shared/api'
import { peekAllZebraEvents, recordRejectedZebraEvent, removeZebraEvent } from './zebraEventQueue'

/** Guards against the `online` listener and the orders cache subscription overlapping. */
let isReplayInProgress = false

/**
 * Replay queued Zebra events, removing each only once the backend has confirmed it
 * (BD-8547).
 *
 * The queue used to be emptied before the first request went out, so any failure during
 * replay lost the event outright — and replay runs on reconnect, which is exactly when a
 * request is most likely to fail. An event now leaves the queue for one of two reasons:
 * the backend took it, or the backend refused it outright.
 *
 * Replaying an event the backend already holds is safe: it deduplicates scans by barcode
 * and missing-crate reports by crate type, so a retry after an ambiguous failure cannot
 * create a second record.
 */
export async function replayZebraEventQueue(): Promise<void> {
  if (isReplayInProgress) {
    return
  }
  isReplayInProgress = true

  try {
    for (const event of peekAllZebraEvents()) {
      try {
        await persistZebraEvent(event)
        removeZebraEvent(event)
      } catch (error) {
        if (!isPermanentRejection(error)) {
          // Leave it queued and stop: the connection is evidently not ready, and the
          // events behind this one would only fail the same way.
          console.error(`Deferred queued zebra event for order ${event.orderId}:`, error)
          return
        }
        // Refused outright. Take it off the queue so it cannot block the events behind
        // it, but keep a record rather than dropping it into a console line.
        console.error(`Backend rejected queued zebra event for order ${event.orderId}:`, error)
        recordRejectedZebraEvent(event, error instanceof Error ? error.message : String(error))
        removeZebraEvent(event)
      }
    }
  } finally {
    isReplayInProgress = false
  }
}
